import { Gauge } from "lucide-react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { Badge } from "../../../client/components/ui/badge";
import useTranslation from "../../../client/hooks/useTranslation";
import type { AdminTradovateQuotaOutput } from "../../../analytics/adminQuotaQuery";

interface Props {
  data: AdminTradovateQuotaOutput;
  onSelectUser?: (userId: string) => void;
}

const TradovateQuotaCard = ({ data, onSelectUser }: Props) => {
  const t = useTranslation();

  // Jauge = l'utilisateur le plus consommateur face à SA limite (~5 000/h par
  // utilisateur Tradovate, mesure du 14/10). Bornée à 100 % pour rester
  // lisible ; le chiffre exact reste affiché à côté.
  const maxUserPercent = Number(data.maxUserPercent ?? 0);
  const barWidth = Math.min(maxUserPercent, 100);
  const isUserOver = maxUserPercent >= 100;

  const barColor = isUserOver
    ? "bg-destructive"
    : data.isUserNearLimit
      ? "bg-amber-500"
      : "bg-primary";

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <Gauge className="h-4 w-4" />
        {t.adminDashboard.quotaTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.quotaHint}
      </p>

      <Card>
        <CardContent className="space-y-4 pt-4">
          <div>
            <p className="text-muted-foreground mb-1 text-xs">
              {t.adminDashboard.quotaWorstUser}
            </p>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="font-mono text-2xl font-semibold">
                {Number(data.maxUserCalls ?? 0)}
                <span className="text-muted-foreground text-base font-normal">
                  {" / "}
                  {Number(data.userLimit ?? 0)}
                </span>
              </span>
              <div className="flex items-center gap-2">
                {isUserOver && (
                  <Badge variant="destructive">{t.adminDashboard.quotaOverLimit}</Badge>
                )}
                {data.isUserNearLimit && !isUserOver && (
                  <Badge variant="outline">{t.adminDashboard.quotaNearLimit}</Badge>
                )}
                <span className="text-muted-foreground font-mono text-sm">
                  {maxUserPercent}%
                </span>
              </div>
            </div>

            <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
              <div
                className={`h-full rounded-full transition-all ${barColor}`}
                style={{ width: `${barWidth}%` }}
              />
            </div>
            <p className="text-muted-foreground mt-2 font-mono text-xs">
              {t.adminDashboard.quotaIpTotal.replace("{calls}", String(data.totalCalls))}
            </p>
          </div>

          {/* Répartition par utilisateur : une PART du pool commun, pas une
              allocation individuelle. Le libellé i18n le dit explicitement. */}
          {data.perUser.length === 0 ? (
            <p className="text-muted-foreground py-2 text-center text-sm">
              {t.adminDashboard.quotaEmpty}
            </p>
          ) : (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs font-medium">
                {t.adminDashboard.quotaPerUserLabel}
              </p>
              {data.perUser.map((user) => (
                <button
                  key={user.userId}
                  type="button"
                  onClick={() => onSelectUser?.(user.userId)}
                  className="hover:bg-accent flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors"
                >
                  <span className="truncate">{user.userEmail ?? user.userId}</span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className="text-muted-foreground text-xs">
                      {user.accountCount}
                    </span>
                    <span className="font-mono text-xs">{user.calls}</span>
                    <span className="text-muted-foreground w-10 text-right font-mono text-xs">
                      {user.sharePercent}%
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateQuotaCard;
